import axios from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { getCertificateCheckout } from "@/services/certificatePaymentsService";
import { navigateAfterCertificatePaid } from "@/utils/certificatePaidNavigation";
import { sleep } from "@/utils/sleep";

/** Interval between polls (dLocal webhook may lag; backend GET syncs from dLocal with its own throttle). */
const POLL_MS = 2500;
/** If rate-limited, wait longer before retrying so we do not amplify 429s. */
const POLL_MS_AFTER_429 = 10_000;
const MAX_ATTEMPTS = 90;

type ReturnPhase = "polling" | "failed" | "mobile-relay";

/**
 * Deep-link scheme used by the mobile app. The portal page is a pure HTTPS relay:
 * when dLocal redirects here with `?mobile=1`, we immediately bounce to this scheme.
 * The Flutter in-app browser (`flutter_web_auth_2`) is watching for any URL on this
 * scheme and auto-closes when it fires. The mobile app then calls `GET :checkoutId`
 * itself (authenticated) to confirm the real status.
 *
 * If this scheme ever changes, coordinate with the mobile app team — the scheme must
 * match what the app registers in `Info.plist` / `AndroidManifest.xml`.
 */
const MOBILE_APP_DEEP_LINK_BASE = "gestionalo://checkout/result";

/**
 * dLocal Go success_url: poll until paid, then resume the certificate wizard on /requests/new.
 * For the mobile flow (`?mobile=1`), skip polling entirely and bounce to a deep link.
 */
export function DlocalReturnPage(): JSX.Element {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<ReturnPhase>("polling");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutId = params.get("checkout_id")?.trim();
    if (!checkoutId) {
      setPhase("failed");
      window.location.replace("/requests/new?certificate_payment=failed");
      return;
    }

    // Mobile relay: no polling, no auth, no status check. Just bounce to the app.
    // The in-app browser has no session anyway (user is logged in on the app, not here).
    // The mobile app is the authenticated party and will call GET :checkoutId after this fires.
    const isMobile = params.get("mobile") === "1";
    if (isMobile) {
      const cancelled = params.get("cancelled") === "1";
      const source = cancelled ? "cancelled" : "success";
      const deepLink = `${MOBILE_APP_DEEP_LINK_BASE}?checkout_id=${encodeURIComponent(
        checkoutId,
      )}&source=${source}`;
      setPhase("mobile-relay");
      window.location.replace(deepLink);
      return;
    }

    let cancelled = false;
    void (async () => {
      for (let i = 0; i < MAX_ATTEMPTS; i++) {
        if (cancelled) return;
        try {
          const row = await getCertificateCheckout(checkoutId);
          if (row.status === "paid") {
            // Shared with the Mercado Pago page: same conversion, same
            // RFQ-versus-wizard branch (see certificatePaidNavigation).
            await navigateAfterCertificatePaid(checkoutId, row);
            return;
          }
          if (row.status === "failed" || row.status === "cancelled") {
            window.location.replace("/requests/new?certificate_payment=failed");
            return;
          }
        } catch (err) {
          const is429 = axios.isAxiosError(err) && err.response?.status === 429;
          await sleep(is429 ? POLL_MS_AFTER_429 : POLL_MS);
          continue;
        }
        await sleep(POLL_MS);
      }
      window.location.replace("/requests/new?certificate_payment=timeout");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (phase === "failed") {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2 px-6 text-center">
        <p className="text-base font-semibold text-foreground">{t("pay.dlocalReturnFailedTitle")}</p>
        <p className="max-w-sm text-sm text-muted-foreground">{t("pay.dlocalReturnFailedMessage")}</p>
      </div>
    );
  }

  if (phase === "mobile-relay") {
    // Fallback UI shown only if the deep-link redirect fails (e.g. user opened the URL
    // in the desktop Safari instead of the mobile app's in-app browser). Normally the
    // `window.location.replace` above fires the custom scheme and this never renders.
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2 px-6 text-center">
        <p className="text-base font-semibold text-foreground">{t("pay.dlocalReturnMobileTitle")}</p>
        <p className="max-w-sm text-sm text-muted-foreground">{t("pay.dlocalReturnMobileMessage")}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-6 text-center">
      <LoadingLogo size="lg" className="mx-auto" />
      <p className="text-base font-semibold text-foreground">{t("pay.dlocalReturnConfirmingTitle")}</p>
      <p className="max-w-sm text-sm text-muted-foreground">{t("pay.dlocalReturnConfirmingMessage")}</p>
    </div>
  );
}
